import React, { useState, ComponentProps, forwardRef } from 'react';
import cn from 'classnames';
import styles from './text-area.module.scss';
import { useUniqueIdEnforcer } from '../checkbox/use-unique-id-enforcer';
import noop from 'lodash/noop';
import { HelperText } from '../text-field/helper-text/helper-text';
import {
  StrengthBar,
  StrengthText,
  StrengthBarConfig,
  DEFAULT_CONFIG,
} from './strength-bar';

type NativeTextArea = Omit<
  React.DetailedHTMLProps<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  >,
  'onChange' | 'value'
>;

export { DEFAULT_CONFIG as defaultStrengthBarConfig };

export type TextAreaProps = NativeTextArea & {
  value: string;
  name: string;
  label?: string;
  placeholder?: string;
  maxLetters?: number;
  onChange?: (text: string, options: { event: any }) => void;
  isDisabled?: boolean;
  className?: string;
  headerClassName?: string;
  helperText?: string;
  hasError?: boolean;
  hasStrengthBar?: boolean;
  strengthBarConfig?: StrengthBarConfig;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      placeholder,
      value,
      name,
      isDisabled,
      className,
      headerClassName,
      helperText = '',
      maxLetters = 200,
      onChange = noop,
      hasError = false,
      hasStrengthBar = false,
      strengthBarConfig = DEFAULT_CONFIG,
      ...otherHttpAttributes
    },
    ref,
  ) => {
    const lettersCount = `${value?.length || 0}/${maxLetters}`;
    const [id, setId] = useState<string | undefined>(otherHttpAttributes.id);
    useUniqueIdEnforcer({ id, setId, prefix: 'text-area' });
    const strengthBar: ComponentProps<typeof StrengthBar> = {
      currentInput: value,
      maxCharacters: maxLetters,
      config: strengthBarConfig,
      disabled: isDisabled,
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const isValid = event.target.value.length <= maxLetters;
      if (isValid) {
        onChange(event.target.value, { event });
      }
    };

    return (
      <div
        data-testid="text-area-wrapper"
        className={cn(styles.box, className)}
      >
        <div
          className={cn(
            styles.header,
            isDisabled && styles.disabled,
            headerClassName,
            hasStrengthBar && styles.hasRows,
          )}
        >
          <label htmlFor={id} className={styles.label}>
            {label}
          </label>
          <div className={styles.infoRow}>
            {hasStrengthBar && <StrengthText {...strengthBar} />}
            <span className={styles.lettersCount}>{lettersCount}</span>
          </div>
        </div>
        <div
          className={cn(
            styles.textAreaContainer,
            isDisabled && styles.isDisabled,
            hasError && styles.error,
          )}
        >
          {hasStrengthBar && <StrengthBar {...strengthBar} />}
          <textarea
            data-testid="text-area"
            aria-multiline="true"
            value={value}
            placeholder={placeholder}
            id={id}
            name={name}
            disabled={isDisabled}
            onChange={handleChange}
            className={cn(styles['text-area'])}
            ref={ref}
            {...otherHttpAttributes}
          />
        </div>
        {helperText && (
          <HelperText name={name} text={helperText} hasError={hasError} />
        )}
      </div>
    );
  },
);
